import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import Select from "../../../../../../components/shared/inputs/Select";
import ResourceSelect from "../../../../../../components/shared/inputs/ResourceSelect";
import { useCustomFields } from "../../../../CustomFields/context";
import { useFiltersConfig as useHomeFiltersConfig } from "../../My/Filters/useFiltersConfig";
import { getSettingsResourceSearchFilters, renderSettingsResourceLabel } from "../../../../../../utils/helpers/settings";

export const useFiltersConfig = () => {
    const { t } = useTranslation();
    const homeFilters = useHomeFiltersConfig();
    const { peopleCustomFields } = useCustomFields();

    return useMemo(() => {
        const config: any = [
            {
                name: "employeeNumber",
                label: t("employee-number"),
                type: "text"
            },
            {
                name: "firstName",
                label: t("first-name"),
                type: "text"
            },
            {
                name: "lastName",
                label: t("last-name"),
                type: "text"
            },
            {
                name: "payGroup",
                label: t("pay-group"),
                type: "resource-select",
                component: (
                    <ResourceSelect
                        labelPropName="description"
                        resourcePath="/pay-group"
                        renderLabel={renderSettingsResourceLabel}
                        mode="multiple"
                        getSearchFilter={getSettingsResourceSearchFilters}
                    />
                ),
            },
            {
                name: "project",
                label: t("project"),
                type: "resource-select",
                component: (
                    <ResourceSelect
                        labelPropName="description"
                        renderLabel={renderSettingsResourceLabel}
                        resourcePath="/project"
                        mode="multiple"
                        getSearchFilter={getSettingsResourceSearchFilters}
                    />
                ),
            },
            {
                name: "union",
                label: t("union"),
                type: "resource-select",
                component: (
                    <ResourceSelect
                        labelPropName="description"
                        renderLabel={renderSettingsResourceLabel}
                        resourcePath="/union"
                        mode="multiple"
                        getSearchFilter={getSettingsResourceSearchFilters}
                    />
                ),
            },
            {
                name: "calculationGroup",
                label: t("calculation-group"),
                type: "resource-select",
                component: (
                    <ResourceSelect
                        labelPropName="description"
                        renderLabel={renderSettingsResourceLabel}
                        resourcePath="/calculation-group"
                        mode="multiple"
                        getSearchFilter={getSettingsResourceSearchFilters}
                    />
                ),
            },
            {
                name: "userGroups",
                label: t("user-groups"),
                type: "resource-select",
                component: (
                    <ResourceSelect
                        labelPropName="description"
                        renderLabel={renderSettingsResourceLabel}
                        resourcePath="/user-group"
                        mode="multiple"
                        getSearchFilter={getSettingsResourceSearchFilters}
                    />
                ),
            },
            {
                name: "groupings-worked",
                label: t("groupings-worked"),
                type: "resource-select",
                component: (
                    <ResourceSelect
                        resourcePath="/grouping"
                        mode="multiple"
                        renderLabel={renderSettingsResourceLabel}
                        getSearchFilter={getSettingsResourceSearchFilters}
                        params={{ pagination: "off" }}
                    />
                ),
            },
            {
                name: "employmentType",
                label: t("employment-type"),
                type: "resource-select",
                component: (
                    <ResourceSelect
                        resourcePath="/user-demographic/employment-type"
                        mode="multiple"
                        renderLabel={renderSettingsResourceLabel}
                        getSearchFilter={getSettingsResourceSearchFilters}
                    />
                ),
            },
            {
                name: "custom-field",
                label: t("home-custom-fields"),
                type: "resource-select",
                component: (
                    <ResourceSelect
                        resourcePath="/custom-field/people"
                        mode="multiple"
                        renderLabel={renderSettingsResourceLabel}
                        getSearchFilter={getSettingsResourceSearchFilters}
                    />
                ),
            },
            {
                name: "salaryOrHourly",
                label: t("salary-or-hourly"),
                type: "select",
                component: (
                    <Select>
                        <Select.Item value="S">{t("salaried")}</Select.Item>
                        <Select.Item value="H">{t("hourly")}</Select.Item>
                    </Select>
                ),
            },
            ...homeFilters
        ];
        if (peopleCustomFields && peopleCustomFields?.length > 0) {
            config.push({
                name: "home-custom-fields",
                children: (peopleCustomFields || []).map((customField) => {
                    return {
                        name: customField.code,
                        label: customField.code,
                        type: customField.type === "select" ? "select" : "text",
                        component: customField.type === "select" ? (
                            <Select mode="multiple">
                                {customField.options.map((option) => (
                                    <Select.Item key={option.value} value={option.value}>
                                        {option.label}
                                    </Select.Item>
                                ))}
                            </Select>
                        ) : undefined,
                    };
                }),
            })
        }
        return config;
    }, [homeFilters, peopleCustomFields, t]);
}